import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  AdminService,
  AdminFinding,
  AdminFindingDetail,
  AdminFindingSeverity,
  AdminFindingState,
  AdminFindingServiceType,
  KNOWN_SERVICE_TYPES,
  ListFindingsParams,
} from '../../core/services/admin.service';

@Component({
  selector: 'app-findings',
  templateUrl: './findings.component.html',
})
export class FindingsComponent implements OnInit, OnDestroy {
  findings: AdminFinding[] = [];
  loading = true;
  loadingMore = false;
  error = '';
  nextCursor: string | null = null;

  selectedFinding: AdminFindingDetail | null = null;
  drawerLoading = false;
  drawerError = '';

  filterSeverity: AdminFindingSeverity | '' = '';
  filterState: AdminFindingState | '' = 'OPEN';
  filterServiceType: AdminFindingServiceType | '' = '';
  filterOrgId = '';

  readonly severities: AdminFindingSeverity[] = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'];
  readonly states: AdminFindingState[] = ['OPEN', 'RESOLVED'];

  /**
   * Filter options for service type — the currently-shipped set PLUS any type
   * actually present in the loaded findings. Driven by data, so newly-supported
   * AWS services appear automatically and never break the filter.
   */
  get serviceTypes(): string[] {
    const seen = new Set<string>([...KNOWN_SERVICE_TYPES, ...this.findings.map(f => f.serviceType).filter(Boolean)]);
    return [...seen].sort();
  }

  constructor(private readonly adminService: AdminService) {}

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {}

  get counts(): { critical: number; high: number; medium: number; open: number; resolved: number } {
    return {
      critical: this.findings.filter(f => f.severity === 'CRITICAL').length,
      high:     this.findings.filter(f => f.severity === 'HIGH').length,
      medium:   this.findings.filter(f => f.severity === 'MEDIUM').length,
      open:     this.findings.filter(f => f.findingState === 'OPEN').length,
      resolved: this.findings.filter(f => f.findingState === 'RESOLVED').length,
    };
  }

  applyFilters(): void {
    this.findings = [];
    this.nextCursor = null;
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = '';
    this.fetch(null);
  }

  loadMore(): void {
    if (!this.nextCursor || this.loadingMore) return;
    this.loadingMore = true;
    this.fetch(this.nextCursor);
  }

  openDrawer(finding: AdminFinding): void {
    this.selectedFinding = null;
    this.drawerError = '';
    this.drawerLoading = true;
    this.adminService.getFinding(finding.findingId, finding.ownerId).subscribe({
      next: detail => {
        this.selectedFinding = detail;
        this.drawerLoading = false;
      },
      error: () => {
        this.drawerError = 'Failed to load finding details.';
        this.drawerLoading = false;
        this.selectedFinding = { ...finding, details: {} };
      },
    });
  }

  closeDrawer(): void {
    this.selectedFinding = null;
    this.drawerLoading = false;
    this.drawerError = '';
  }

  severityClass(severity: string): string {
    const map: Record<string, string> = {
      CRITICAL: 'bg-red-100 text-red-700 border-red-200',
      HIGH:     'bg-orange-100 text-orange-700 border-orange-200',
      MEDIUM:   'bg-amber-100 text-amber-700 border-amber-200',
      LOW:      'bg-sky-100 text-sky-700 border-sky-200',
      INFO:     'bg-slate-100 text-slate-600 border-slate-200',
    };
    return map[severity] ?? 'bg-slate-100 text-slate-600 border-slate-200';
  }

  stateClass(state: string): string {
    return state === 'OPEN'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : 'bg-zinc-100 text-zinc-500 border-zinc-200';
  }

  serviceTypeClass(type: string): string {
    const map: Record<string, string> = {
      LAMBDA:         'bg-orange-50 text-orange-700 border-orange-200',
      GLUE:           'bg-sky-50 text-sky-700 border-sky-200',
      PIPELINE:       'bg-violet-50 text-violet-700 border-violet-200',
      STEP_FUNCTIONS: 'bg-purple-50 text-purple-700 border-purple-200',
      ECS_SERVICE:    'bg-teal-50 text-teal-700 border-teal-200',
      RDS_INSTANCE:   'bg-orange-50 text-orange-700 border-orange-200',
    };
    return map[type] ?? 'bg-slate-50 text-slate-600 border-slate-200';
  }

  /** Friendly label for any service type (known or new) — never breaks on unknowns. */
  serviceTypeLabel(type: string): string {
    const map: Record<string, string> = {
      LAMBDA: 'Lambda', GLUE: 'Glue', PIPELINE: 'CodePipeline', STEP_FUNCTIONS: 'Step Functions',
      ECS_SERVICE: 'ECS', RDS_INSTANCE: 'RDS', API_GATEWAY: 'API Gateway', EC2_INSTANCE: 'EC2',
      COGNITO: 'Cognito', APPSYNC: 'AppSync', DYNAMODB: 'DynamoDB', SQS: 'SQS',
      EVENTBRIDGE: 'EventBridge', ALB: 'ALB', SNS: 'SNS', CLOUDFRONT: 'CloudFront',
    };
    if (!type) return '—';
    return map[type.toUpperCase()] ?? type.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
  }

  /** Short account+region scope for a finding, for support triage. */
  scopeLabel(finding: AdminFinding): string {
    return `${finding.accountId} · ${finding.region}`;
  }

  /** Provider-aware label for the account/subscription identifier row. */
  accountLabel(finding: AdminFinding): string {
    const provider = finding.provider ?? (finding.serviceType?.toUpperCase().startsWith('AZURE_') ? 'AZURE' : 'AWS');
    return provider === 'AZURE' ? 'Azure subscription' : 'AWS account';
  }

  detailsJson(details: Record<string, unknown>): string {
    try { return JSON.stringify(details, null, 2); } catch { return '{}'; }
  }

  objectKeys(obj: Record<string, unknown>): string[] {
    return Object.keys(obj);
  }

  private fetch(cursor: string | null): void {
    const params: ListFindingsParams = { limit: 100 };
    if (this.filterSeverity)    params.severity    = this.filterSeverity;
    if (this.filterState)       params.state       = this.filterState;
    if (this.filterServiceType) params.serviceType = this.filterServiceType;
    if (this.filterOrgId.trim()) params.orgId      = this.filterOrgId.trim();
    if (cursor)                 params.cursor      = cursor;

    this.adminService.listFindings(params).subscribe({
      next: res => {
        this.findings   = cursor ? [...this.findings, ...res.findings] : res.findings;
        this.nextCursor = res.nextCursor;
        this.loading    = false;
        this.loadingMore = false;
      },
      error: () => {
        this.error      = 'Failed to load findings.';
        this.loading    = false;
        this.loadingMore = false;
      },
    });
  }
}
